import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SolarScene from "@/components/SolarScene";
import FourPillarsEngine from "@/components/FourPillars";
import FinalCta from "@/components/FinalCta";
import ProjectsSection from "@/components/ProjectsSection";
import ReviewsSection from "@/components/ReviewsSection";
import FollowUpPanel from "@/components/FollowUpPanel";
import ReportPreview from "@/components/ReportPreview";
import { useSite } from "@/context/SiteContext";
import { SERVICES } from "@/lib/services";

const BUSINESS_IDS = ["commercial", "supercharger", "smart-panel", "ev", "hvac", "roofing", "construction", "care"];

const lineVariants = {
  hidden: { y: "112%" },
  show: (i) => ({ y: 0, transition: { delay: 0.1 + i * 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] } }),
};

const JOURNEY = [
  { title: "Start with your property", body: "Tell us the property type. Enter the address and confirm the actual building." },
  { title: "Bring your numbers", body: "Monthly and annual electric use — a recent bill is enough to begin; interval data can follow." },
  { title: "Shape the concept", body: "Roof or land details, layout ideas, and the priorities that matter to your operation." },
  { title: "Get a saved assessment", body: "Contact, consent, and a project reference your team can act on." },
];

export default function Business() {
  const { config, openLead } = useSite();
  const services = SERVICES.filter((s) => BUSINESS_IDS.includes(s.id) && config.enabledServices.includes(s.id));
  return (
    <>
      <Seo
        title="Commercial solar & business energy | Solar Black Belt"
        description="Bring rooftop, carport and ground-mount solar, storage and electrical planning together for your facility — with bill-based calculators you can run before you ever talk to us."
      />
      <section className="hero" style={{ paddingBottom: 26 }}>
        <div className="wrap">
          <div className="hero-grid" style={{ gridTemplateColumns: "1.15fr 1fr" }}>
            <div className="hero-copy">
              <div className="badge">
                <span className="dot" /> WE INSTALL COMMERCIAL SOLAR · FOR BUSINESS
              </div>
              <h1 data-testid="business-heading">
                <span className="hero-line">
                  <motion.span custom={0} variants={lineVariants} initial="hidden" animate="show">Your building,</motion.span>
                </span>
                <span className="hero-line">
                  <motion.span custom={1} variants={lineVariants} initial="hidden" animate="show"><em style={{ fontStyle: "normal", color: "var(--teal)" }}>working harder.</em></motion.span>
                </span>
              </h1>
              <p style={{ marginTop: 22 }}>
                Roof, land, load and tariff — one connected plan. Run the numbers yourself, then request an assessment that keeps every answer attached.
              </p>
              <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
                <button className="btn lime" data-testid="business-cta" onClick={() => openLead("commercial", "Quote request", { audience: "business" })}>
                  Get a quote <span>↗</span>
                </button>
                <a className="btn light" href={config.phoneHref}>{config.phone}</a>
                <Link className="btn light" to="/projects" data-testid="business-jobs-link">See jobs we've done ↗</Link>
              </div>
            </div>
            <div className="hero-art" style={{ minHeight: 380 }}>
              <div className="scene-label"><span className="dot" /> COMMERCIAL ENERGY CONCEPT · DRAG TO ROTATE</div>
              <SolarScene kind="commercial" testId="business-scene" />
              <div className="floating one">
                <span>ONE CONNECTED ENERGY PLAN</span>
                <strong>Solar + storage + loads</strong>
                <span>Built around your operating schedule</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 54 }}>
        <div className="wrap">
          <div className="section-heading">
            <div>
              <span className="eyebrow">THE BUSINESS JOURNEY</span>
              <h2>Four steps, your pace.</h2>
            </div>
            <p>Explore first. Nothing is gated until you choose to request an assessment.</p>
          </div>
          <div className="journey" data-testid="business-journey">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.title} delay={i * 0.08}>
                <div className="journey-step">
                  <span className="num">{i + 1}</span>
                  <b>{j.title}</b>
                  <p>{j.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FourPillarsEngine />

      <section className="section" style={{ paddingTop: 30 }}>
        <div className="wrap split2" style={{ alignItems: "stretch" }}>
          <Reveal>
            <ReportPreview />
            <button className="btn" style={{ marginTop: 20 }} onClick={() => openLead("commercial", "Business energy assessment", { audience: "business" })}>
              Prefer a walkthrough first? Request an assessment <span>↗</span>
            </button>
          </Reveal>
          <Reveal delay={0.08}>
            <FollowUpPanel />
          </Reveal>
        </div>
      </section>

      <ProjectsSection />

      <ReviewsSection />

      <section className="section" id="business-services" style={{ background: "var(--mint)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
        <div className="wrap">
          <div className="section-heading">
            <div>
              <span className="eyebrow">A PLAN THAT WORKS TOGETHER</span>
              <h2>Every piece of the property, in one place.</h2>
            </div>
            <p>Explore the business services that fit your building and your bill.</p>
          </div>
          <div className="cards-grid" data-testid="business-services">
            {services.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <div className="service-card" style={{ height: "100%" }}>
                  <span className="icon" aria-hidden="true">{s.icon}</span>
                  <h3>{s.name}</h3>
                  <p>{s.description}</p>
                  <Link className="go" to={`/services/${s.id}`} data-testid={`business-link-${s.id}`}>
                    Explore {s.name.toLowerCase()} ↗
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <div style={{ height: 54 }} />
      <FinalCta title={["Let’s put your building", "to work."]} service="commercial" context="Quote request" />
    </>
  );
}
